import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faFileEdit, faTruckArrowRight } from '@fortawesome/free-solid-svg-icons';

import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';
import { ActivityIndicator } from '../components/custom/ActivityIndicator';

import {
    fetch_all_subcontractors,
    add_subcontractor,
    update_subcontractor,
    delete_subcontractor,
} from '../api/subcontractors';

import './css/GlobalStyles.css';
import './css/DriversStyles.css';
import './css/LeftNavigation.css';
import './css/Styles.css';

const emptyForm = {
    company_name: '',
    address: '',
    postcode: '',
    contract_number: '',
    contract_name: '',
};

const SubContractorsPage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { isDev, permissions } = useAuthProvider();
    const navigate = useNavigate();

    const canView = isDev || permissions.perm_subcontractors_view;
    const canEdit = isDev || permissions.perm_subcontractors_edit;

    const [isLoading, setIsLoading] = useState(false);
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    // ── Permission gate ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!canView) {
            navigate('/');
        }
    }, [canView, navigate]);

    // ── Load list ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (canView) loadList();
    }, [canView]);

    const loadList = async () => {
        setIsLoading(true);
        try {
            const result = await fetch_all_subcontractors();
            if (result.status === 200) {
                const sorted = (result.data?.data || []).sort((a, b) => a.company_name.localeCompare(b.company_name));
                setList(sorted);
            } else {
                showAlert({ message: 'Failed to load sub-contractors', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────────────────
    const openAdd = () => {
        setIsAdding(true);
        setSelected(null);
        setForm(emptyForm);
        setErrors({});
        setShowModal(true);
    };

    const openEdit = (record) => {
        setIsAdding(false);
        setSelected(record);
        setForm({
            company_name: record.company_name || '',
            address: record.address || '',
            postcode: record.postcode || '',
            contract_number: record.contract_number || '',
            contract_name: record.contract_name || '',
        });
        setErrors({});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelected(null);
        setForm(emptyForm);
        setErrors({});
    };

    // ── Validation ───────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.company_name.trim()) errs.company_name = 'Company name is required.';
        return errs;
    };

    // ── Save ─────────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        setIsLoading(true);
        const payload = {
            company_name: form.company_name.trim(),
            address: form.address.trim() || null,
            postcode: form.postcode.trim().toUpperCase() || null,
            contract_number: form.contract_number.trim() || null,
            contract_name: form.contract_name.trim() || null,
        };

        try {
            let result;
            if (isAdding) {
                result = await add_subcontractor(payload);
            } else {
                result = await update_subcontractor(selected.id, payload);
            }

            if (result.status === 200) {
                showAlert({ message: isAdding ? 'Sub-contractor added.' : 'Sub-contractor updated.', type: 'success', buttons: [{ label: 'Close' }] });
                closeModal();
                loadList(); // re-fetches and re-sorts
            } else {
                showAlert({ message: result.data?.error || 'Save failed.', type: 'danger', buttons: [{ label: 'Close' }] });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ── Delete ───────────────────────────────────────────────────────────────────
    const handleDelete = (record) => {
        showAlert({
            message: `Delete ${record.company_name}? This cannot be undone.`,
            type: 'warning',
            buttons: [
                { label: 'Cancel', type: 'secondary' },
                {
                    label: 'Delete',
                    type: 'danger',
                    onClick: async () => {
                        setIsLoading(true);
                        try {
                            const result = await delete_subcontractor(record.id);
                            if (result.status === 200) {
                                setList(prev => prev.filter(r => r.id !== record.id));
                            } else {
                                showAlert({ message: 'Failed to delete.', type: 'danger', buttons: [{ label: 'Close' }] });
                            }
                        } finally {
                            setIsLoading(false);
                        }
                    },
                },
            ],
        });
    };

    // ── Styles ───────────────────────────────────────────────────────────────────
    const cardBg = theme === 'dark' ? '#1c1c1c' : '#fff';
    const cardBorder = theme === 'dark' ? '1px solid #444' : '1px solid #ddd';
    const textColor = theme === 'dark' ? '#fff' : '#333';
    const mutedColor = theme === 'dark' ? '#aaa' : '#666';
    const inputBg = theme === 'dark' ? '#333' : '#fff';

    return (
        <div id={theme} className="container-fluid closed-drivers-container">
        <div style={{ padding: '20px', paddingTop: '40px', color: textColor }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FontAwesomeIcon icon={faTruckArrowRight} style={{ width: 24 }} />
                        Sub-Contractors
                    </h3>
                    <p style={{ margin: '4px 0 0', color: mutedColor, fontSize: 13 }}>
                        Manage regularly-used sub-contracting companies
                    </p>
                </div>
                {canEdit && (
                    <Button variant="primary" size="sm" onClick={openAdd}>
                        <FontAwesomeIcon icon={faPlus} style={{ width: 12, marginRight: 6 }} />
                        Add Sub-Contractor
                    </Button>
                )}
            </div>

            {/* List */}
            {isLoading ? (
                <div style={{ textAlign: 'center', marginTop: '20vh' }}>
                    <ActivityIndicator />
                </div>
            ) : list.length === 0 ? (
                <div style={{ textAlign: 'center', marginTop: '15vh', color: mutedColor }}>
                    <FontAwesomeIcon icon={faTruckArrowRight} style={{ width: 48, fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                    <p>No sub-contractors on record.</p>
                    {canEdit && <Button variant="outline-primary" onClick={openAdd}>Add First Sub-Contractor</Button>}
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                    {list.map(record => (
                        <div
                            key={record.id}
                            style={{
                                background: cardBg,
                                border: cardBorder,
                                borderRadius: 10,
                                padding: '16px 20px',
                                boxShadow: theme === 'dark' ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ fontWeight: 700, fontSize: 15 }}>{record.company_name}</div>
                                {canEdit && (
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                            title="Edit"
                                            onClick={() => openEdit(record)}
                                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#1997eb', fontSize: 15 }}
                                        >
                                            <FontAwesomeIcon icon={faFileEdit} style={{ width: 14 }} />
                                        </button>
                                        <button
                                            title="Delete"
                                            onClick={() => handleDelete(record)}
                                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#e74c3c', fontSize: 15 }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} style={{ width: 12 }} />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: 8, fontSize: 13, color: mutedColor, lineHeight: 1.8 }}>
                                {record.address && <div>{record.address}</div>}
                                {record.postcode && <div><strong>Postcode:</strong> {record.postcode}</div>}
                                {record.contract_name && <div><strong>Contract:</strong> {record.contract_name}</div>}
                                {record.contract_number && <div><strong>Ref:</strong> {record.contract_number}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add / Edit Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: cardBg, border: cardBorder, borderRadius: 12,
                        padding: 32, minWidth: 360, maxWidth: 520, width: '90%',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.2)', color: textColor,
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h5 style={{ margin: 0, fontWeight: 700 }}>
                                {isAdding ? 'Add Sub-Contractor' : 'Edit Sub-Contractor'}
                            </h5>
                            <button onClick={closeModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: textColor }}>
                                <FontAwesomeIcon icon={faTimes} style={{ width: 14 }} />
                            </button>
                        </div>

                        {[
                            { key: 'company_name', label: 'Company Name *', placeholder: 'Company name' },
                            { key: 'address', label: 'Address', placeholder: 'Street address' },
                            { key: 'postcode', label: 'Postcode', placeholder: 'e.g. WF1 1AA' },
                            { key: 'contract_name', label: 'Contract Name', placeholder: 'Contract description' },
                            { key: 'contract_number', label: 'Contract Number', placeholder: 'Contract reference number' },
                        ].map(({ key, label, placeholder }) => (
                            <Form.Group key={key} style={{ marginBottom: 14 }}>
                                <Form.Label style={{ fontWeight: 500, fontSize: 13, marginBottom: 4 }}>{label}</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder={placeholder}
                                    value={form[key]}
                                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                                    isInvalid={!!errors[key]}
                                    style={{ background: inputBg, color: textColor, borderColor: errors[key] ? '#e74c3c' : undefined }}
                                />
                                {errors[key] && <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 2 }}>{errors[key]}</div>}
                            </Form.Group>
                        ))}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
                            <Button variant="primary" onClick={handleSave} disabled={isLoading}>
                                {isLoading ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
};

export default SubContractorsPage;
